// web-frontend/src/shared/constants/Design.js
// 🔥 웹용으로 수정 - react-native 제거

/**
 * Design System - 웹 전용
 * 반응형 디자인을 위한 브레이크포인트와 유틸리티
 */

// 화면 크기 가져오기 (웹용)
const getScreenDimensions = () => {
  if (typeof window !== 'undefined') {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }
  return {
    width: 1200,
    height: 800,
  };
};

const { width, height } = getScreenDimensions();

// ====================================
// 📱 SCREEN DIMENSIONS
// ====================================
const SCREEN_WIDTH = width;
const SCREEN_HEIGHT = height;

// ====================================
// 📐 BREAKPOINTS (반응형 웹)
// ====================================
const BREAKPOINTS = {
  mobile: 480,
  tablet: 768,
  desktop: 1024,
  wide: 1440,
  ultraWide: 1920,
};

// ====================================
// 🎨 SPACING SCALE
// ====================================
const SPACING = {
  xs: 4,
  sm: 8,
  md: 12,
  base: 16,
  lg: 20,
  xl: 24,
  xxl: 32,
  xxxl: 48,
  huge: 64,
};

// ====================================
// 🔤 TYPOGRAPHY SCALE
// ====================================
const TYPOGRAPHY = {
  sizes: {
    xs: 11,
    sm: 13,
    base: 15,
    md: 16,
    lg: 18,
    xl: 20,
    xxl: 24,
    xxxl: 32,
    display: 40,
    hero: 56,
  },
  weights: {
    light: '300',
    regular: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
    black: '900',
  },
  lineHeights: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
    loose: 2,
  },
};

// ====================================
// 🎨 BORDER RADIUS
// ====================================
const BORDER_RADIUS = {
  xs: 2,
  sm: 4,
  md: 8,
  base: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
  round: '50%',
  pill: 9999,
};

// ====================================
// 🌈 COLORS (간단 버전)
// ====================================
const COLORS = {
  primary: '#2E7D32',
  secondary: '#4CAF50',
  accent: '#FFD700',
  
  // Status
  success: '#4CAF50',
  warning: '#FF9800',
  error: '#F44336',
  info: '#2196F3',
  
  // Grayscale
  white: '#FFFFFF',
  black: '#000000',
  gray: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  
  // Background
  background: {
    primary: '#FFFFFF',
    secondary: '#F5F5F5',
    tertiary: '#EEEEEE',
    dark: '#0F0F0F',
  },
  
  // Text
  text: {
    primary: '#000000',
    secondary: '#666666',
    tertiary: '#999999',
    inverse: '#FFFFFF',
  },
};

// ====================================
// 💫 SHADOWS
// ====================================
const SHADOWS = {
  none: 'none',
  xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
  sm: '0 2px 4px rgba(0, 0, 0, 0.1)',
  md: '0 4px 6px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px rgba(0, 0, 0, 0.15)',
  xxl: '0 25px 50px rgba(0, 0, 0, 0.25)',
  inner: 'inset 0 2px 4px rgba(0, 0, 0, 0.06)',
};

// ====================================
// ⚡ TRANSITIONS
// ====================================
const TRANSITIONS = {
  fast: '150ms ease',
  normal: '300ms ease',
  slow: '500ms ease',
  spring: '500ms cubic-bezier(0.68, -0.55, 0.265, 1.55)',
};

// ====================================
// 📏 LAYOUT
// ====================================
const LAYOUT = {
  headerHeight: 64,
  footerHeight: 56,
  sidebarWidth: 280,
  sidebarCollapsedWidth: 64,
  maxContentWidth: 1200,
  containerPadding: SPACING.base,
};

// ====================================
// 🎯 Z-INDEX SCALE
// ====================================
const Z_INDEX = {
  base: 0,
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
  notification: 1080,
};

// ====================================
// 🔧 UTILITY FUNCTIONS
// ====================================

/**
 * 미디어 쿼리 생성 헬퍼
 */
const mediaQuery = {
  mobile: `@media (max-width: ${BREAKPOINTS.mobile}px)`,
  tablet: `@media (max-width: ${BREAKPOINTS.tablet}px)`,
  desktop: `@media (min-width: ${BREAKPOINTS.desktop}px)`,
  wide: `@media (min-width: ${BREAKPOINTS.wide}px)`,
};

/**
 * 반응형 값 계산
 */
const responsive = (mobile, tablet = mobile, desktop = tablet) => {
  const currentWidth = SCREEN_WIDTH;
  
  if (currentWidth < BREAKPOINTS.mobile) return mobile;
  if (currentWidth < BREAKPOINTS.tablet) return tablet;
  return desktop;
};

/**
 * spacing 값을 픽셀로 변환
 */
const getSpacing = (multiplier = 1) => {
  return SPACING.base * multiplier;
};

/**
 * 그림자 스타일 생성
 */
const getShadow = (level = 'md') => {
  return SHADOWS[level] || SHADOWS.md;
};

// ====================================
// 📦 EXPORT
// ====================================
const Design = {
  // Dimensions
  SCREEN_WIDTH,
  SCREEN_HEIGHT,
  BREAKPOINTS,
  
  // Spacing
  SPACING,
  getSpacing,
  
  // Typography
  TYPOGRAPHY,
  
  // Borders
  BORDER_RADIUS,
  
  // Colors
  COLORS,
  
  // Shadows
  SHADOWS,
  getShadow,
  
  // Transitions
  TRANSITIONS,
  
  // Layout
  LAYOUT,
  
  // Z-Index
  Z_INDEX,
  
  // Utilities
  mediaQuery,
  responsive,
};

export default Design;

// Named exports
export {
  SCREEN_WIDTH,
  SCREEN_HEIGHT,
  BREAKPOINTS,
  SPACING,
  TYPOGRAPHY,
  BORDER_RADIUS,
  COLORS,
  SHADOWS,
  TRANSITIONS,
  LAYOUT,
  Z_INDEX,
  mediaQuery,
  responsive,
  getSpacing,
  getShadow,
};